import { isValidString } from '../../../common/CommonUtilsMethods';
import { formatEpochSecondsForDisplayV2 } from '../../../common/TimeUtils';

enum WorkTaskQuestionType {
  TEXT_FIELD = 'TEXT_FIELD',
  TIME_HH_MM = 'TIME_HH_MM',
  DATE_YYYY_MM_DD = 'DATE_YYYY_MM_DD',
  CANADIAN_POSTAL_CODE = 'CANADIAN_POSTAL_CODE',
  NUMBER = 'NUMBER',
  FILE_TYPE = 'FILE_TYPE',
  FILE_TYPE_WITH_EXPIRY = 'FILE_TYPE_WITH_EXPIRY',
  DROP_DOWN_SELECT = 'DROP_DOWN_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  ACTIVITY_TIME_GRID = 'ACTIVITY_TIME_GRID',
  TABLE_GRID = 'TABLE_GRID',
  STATIC_TEXT = 'STATIC_TEXT',
  ATTRIBUTE = 'ATTRIBUTE',
}

enum WorkTaskAttributeAnswerType {
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  RATING = 'RATING',
}

interface WorkTaskAttributeSelectOptionResponseDTO {
  readonly optionId: string;
  readonly label: string;
}

interface WorkTaskSelectOptionRequestDTO {
  readonly optionId?: string | null;
  readonly label: string;
}

interface WorkTaskSelectOptionResponseDTO {
  readonly optionId: string;
  readonly label: string;
}

enum WorkTaskQuestionSubTypeOne {
  NUMERIC = 'NUMERIC',
  FREE_FORM_TEXT = 'FREE_FORM_TEXT',
}

enum RequiredAttributeType {
  WORKER_SIG = 'WORKER_SIG',
  CLIENT_SIG = 'CLIENT_SIG',
}

enum WorkTaskTemplatePurpose {
  WORK_TASK = 'WORK_TASK',
  CARE_PLAN = 'CARE_PLAN',
  JOBBER_ONBOARDING = 'JOBBER_ONBOARDING',
}

enum WorkTaskPdfSectionVisibility {
  INCLUDE_IN_PDF = 'INCLUDE_IN_PDF',
  EXCLUDE_FROM_PDF = 'EXCLUDE_FROM_PDF',
}

enum WorkTaskFileDocumentOwnership {
  WORKER_OWNED = 'WORKER_OWNED',
  CLIENT_OWNED = 'CLIENT_OWNED',
}

interface RequiredAttributeResponseDTO {
  readonly requiredAttributeType: RequiredAttributeType | string;
}

interface WorkTaskTableColumnDefinitionRequestDTO {
  readonly columnId?: string | null;
  readonly columnLabel: string;
  readonly columnOrder?: number;
}

interface WorkTaskTableColumnDefinitionResponseDTO {
  readonly columnId: string;
  readonly columnLabel: string;
  readonly columnOrder: number;
}

interface WorkTaskActivityTypeDefinitionRequestDTO {
  readonly activityTypeId?: string | null;
  readonly activityLabel: string;
}

interface WorkTaskActivityTypeDefinitionResponseDTO {
  readonly activityTypeId: string;
  readonly activityLabel: string;
}

interface WorkTaskSectionConditionRequestDTO {
  readonly targetSectionName: string;
  readonly operator: string;
  readonly expectedValues: readonly string[];
}

interface WorkTaskQuestionVisibilityConditionRequestDTO {
  readonly sourceQuestionId: string;
  readonly operator: string;
  readonly expectedValues: readonly string[];
}

interface WorkTaskQuestionRequestDTO {
  readonly questionId?: string | null;
  readonly question: string;
  readonly questionType: WorkTaskQuestionType;
  readonly questionSubTypeOne?: WorkTaskQuestionSubTypeOne | null;
  readonly required: boolean;
  readonly description?: string | null;
  readonly options?: readonly string[] | null;
  readonly selectOptions?: readonly WorkTaskSelectOptionRequestDTO[] | null;
  readonly showSectionsWhen?: readonly WorkTaskSectionConditionRequestDTO[] | null;
  readonly visibleWhen?: WorkTaskQuestionVisibilityConditionRequestDTO | null;
  readonly activityTypes?: readonly WorkTaskActivityTypeDefinitionRequestDTO[] | null;
  readonly tableColumns?: readonly WorkTaskTableColumnDefinitionRequestDTO[] | null;
  readonly documentSubTypeUniqueIdentifier?: string | null;
  readonly documentOwnership?: WorkTaskFileDocumentOwnership | string | null;
  readonly staticText?: string | null;
  readonly attributeId?: string | null;
}

interface WorkTaskFormSectionRequestDTO {
  readonly sectionId?: string | null;
  readonly sectionTitle: string;
  readonly questions: readonly WorkTaskQuestionRequestDTO[];
  readonly isDefaultSection: boolean;
  readonly pdfSectionVisibility?: WorkTaskPdfSectionVisibility;
}

interface CreateWorkTaskTemplateRequestDTO {
  readonly templateName: string;
  readonly sections: readonly WorkTaskFormSectionRequestDTO[];
  readonly requiredAttributes?: readonly RequiredAttributeType[];
  readonly templatePurpose?: WorkTaskTemplatePurpose | string | null;
  readonly workTaskDisplayConfigTemplateId?: string | null;
}

interface UpdateWorkTaskTemplateRequestDTO {
  readonly templateId: string;
  readonly templateName: string;
  readonly sections: readonly WorkTaskFormSectionRequestDTO[];
  readonly requiredAttributes?: readonly RequiredAttributeType[];
  readonly workTaskDisplayConfigTemplateId?: string | null;
}

interface WorkTaskSectionConditionResponseDTO {
  readonly targetSectionId: string;
  readonly operator: string;
  readonly expectedValues: readonly string[];
}

interface WorkTaskQuestionVisibilityConditionResponseDTO {
  readonly sourceQuestionId: string;
  readonly operator: string;
  readonly expectedValues: readonly string[];
}

interface WorkTaskQuestionResponseDTO {
  readonly questionId: string;
  readonly question: string;
  readonly questionType: WorkTaskQuestionType;
  readonly questionSubTypeOne?: WorkTaskQuestionSubTypeOne | string | null;
  readonly required: boolean;
  readonly description?: string | null;
  readonly options?: readonly string[] | null;
  readonly selectOptions?: readonly WorkTaskSelectOptionResponseDTO[] | null;
  readonly showSectionsWhen?: readonly WorkTaskSectionConditionResponseDTO[] | null;
  readonly visibleWhen?: WorkTaskQuestionVisibilityConditionResponseDTO | null;
  readonly activityTypes?: readonly WorkTaskActivityTypeDefinitionResponseDTO[] | null;
  readonly tableColumns?: readonly WorkTaskTableColumnDefinitionResponseDTO[] | null;
  readonly documentSubTypeUniqueIdentifier?: string | null;
  readonly documentOwnership?: WorkTaskFileDocumentOwnership | string | null;
  readonly staticText?: string | null;
  readonly attributeId?: string | null;
  readonly attributeAnswerType?: WorkTaskAttributeAnswerType | string | null;
  readonly attributeSelectOptions?: readonly WorkTaskAttributeSelectOptionResponseDTO[] | null;
  readonly attributeRatingMin?: number | null;
  readonly attributeRatingMax?: number | null;
}

interface WorkTaskFormSectionResponseDTO {
  readonly sectionId: string;
  readonly sectionTitle: string;
  readonly questions: readonly WorkTaskQuestionResponseDTO[];
  readonly isDefaultSection: boolean;
  readonly pdfSectionVisibility?: WorkTaskPdfSectionVisibility;
}

interface WorkTaskTemplateMetaData {
  readonly submissionDateTimeEpochSeconds?: number | null;
  readonly submissionDateTime?: string | null;
  readonly updatedDateTimeEpoch?: number | null;
  readonly updatedDateTime?: string | null;
  readonly version?: number | null;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

interface WorkTaskTemplateResponseDTO {
  readonly agencyName: string;
  readonly templateName: string;
  readonly templateId: string;
  readonly templateCreatorId: string;
  readonly templateCreatorFirstName?: string | null;
  readonly templateCreatorLastName?: string | null;
  readonly templateVersion: number;
  readonly templateStatus?: string;
  readonly templatePurpose?: WorkTaskTemplatePurpose | string;
  readonly workTaskDisplayConfigTemplateId?: string | null;
  readonly jobberOnboardingTemplateType?: string | null;
  readonly requiredAttributes?: readonly RequiredAttributeResponseDTO[];
  readonly sections: readonly WorkTaskFormSectionResponseDTO[];
  readonly metaData: WorkTaskTemplateMetaData;
}

interface WorkTaskQuestionTypeOption {
  readonly value: WorkTaskQuestionType;
  readonly label: string;
}

const formatWorkTaskTemplateEpochMetaDisplay = (
  epochSeconds: number | null | undefined,
  dateTimeString: string | null | undefined
): string => {
  if (epochSeconds != null && epochSeconds > 0) {
    return formatEpochSecondsForDisplayV2(epochSeconds);
  }
  if (isValidString(dateTimeString)) {
    return dateTimeString ?? '';
  }
  return '';
};

const formatWorkTaskTemplateCreatedDisplay = (
  metaData: WorkTaskTemplateMetaData | null | undefined
): string => {
  if (metaData == null) {
    return '-';
  }
  const fromEpochFields: string = formatWorkTaskTemplateEpochMetaDisplay(
    metaData.submissionDateTimeEpochSeconds,
    metaData.submissionDateTime
  );
  if (isValidString(fromEpochFields)) {
    return fromEpochFields;
  }
  if (isValidString(metaData.createdAt)) {
    return new Date(metaData.createdAt).toLocaleString();
  }
  return '-';
};

const formatWorkTaskTemplateUpdatedDisplay = (
  metaData: WorkTaskTemplateMetaData | null | undefined
): string => {
  if (metaData == null) {
    return '-';
  }
  const fromEpochFields: string = formatWorkTaskTemplateEpochMetaDisplay(
    metaData.updatedDateTimeEpoch,
    metaData.updatedDateTime
  );
  if (isValidString(fromEpochFields)) {
    return fromEpochFields;
  }
  if (isValidString(metaData.updatedAt)) {
    return new Date(metaData.updatedAt).toLocaleString();
  }
  return '-';
};

/**
 * Human-readable label for templatePurpose in agency Task Templates lists.
 */
const getWorkTaskTemplatePurposeDisplayLabel = (
  templatePurpose: WorkTaskTemplatePurpose | string | null | undefined
): string => {
  if (
    templatePurpose === WorkTaskTemplatePurpose.JOBBER_ONBOARDING
    || templatePurpose === 'JOBBER_ONBOARDING'
  ) {
    return 'Worker onboarding';
  }
  if (
    templatePurpose === WorkTaskTemplatePurpose.CARE_PLAN
    || templatePurpose === 'CARE_PLAN'
  ) {
    return 'Care plan';
  }
  return 'Worker task';
};

const isWorkerTaskTemplatePurpose = (
  templatePurpose: WorkTaskTemplatePurpose | string | null | undefined
): boolean => {
  if (
    templatePurpose === WorkTaskTemplatePurpose.CARE_PLAN
    || templatePurpose === 'CARE_PLAN'
    || templatePurpose === WorkTaskTemplatePurpose.JOBBER_ONBOARDING
    || templatePurpose === 'JOBBER_ONBOARDING'
  ) {
    return false;
  }
  return true;
};

const formatWorkTaskTemplateCreatorDisplay = (
  template: WorkTaskTemplateResponseDTO
): string => {
  const nameParts: string[] = [];
  if (isValidString(template.templateCreatorFirstName)) {
    nameParts.push(template.templateCreatorFirstName.trim());
  }
  if (isValidString(template.templateCreatorLastName)) {
    nameParts.push(template.templateCreatorLastName.trim());
  }
  const fullName: string = nameParts.join(' ').trim();
  if (isValidString(fullName)) {
    return fullName;
  }
  return template.templateCreatorId;
};

const shouldDisplayWorkTaskTemplateLastUpdated = (
  template: WorkTaskTemplateResponseDTO
): boolean => {
  if (template.templateVersion <= 1) {
    return false;
  }
  const createdEpoch: number = template.metaData?.submissionDateTimeEpochSeconds ?? 0;
  const updatedEpoch: number = template.metaData?.updatedDateTimeEpoch ?? 0;
  if (createdEpoch > 0 && updatedEpoch > 0 && createdEpoch === updatedEpoch) {
    return false;
  }
  const createdDisplay: string = formatWorkTaskTemplateCreatedDisplay(template.metaData);
  const updatedDisplay: string = formatWorkTaskTemplateUpdatedDisplay(template.metaData);
  if (
    isValidString(createdDisplay) &&
    isValidString(updatedDisplay) &&
    createdDisplay === updatedDisplay
  ) {
    return false;
  }
  return isValidString(updatedDisplay) && updatedDisplay !== '-';
};

const WORK_TASK_QUESTION_TYPES: readonly WorkTaskQuestionTypeOption[] = [
  { value: WorkTaskQuestionType.TEXT_FIELD, label: 'Text Input' },
  { value: WorkTaskQuestionType.TIME_HH_MM, label: 'Time (HH:mm, 24-hour)' },
  { value: WorkTaskQuestionType.DATE_YYYY_MM_DD, label: 'Date (YYYY-MM-DD)' },
  { value: WorkTaskQuestionType.CANADIAN_POSTAL_CODE, label: 'Canadian Postal Code' },
  { value: WorkTaskQuestionType.NUMBER, label: 'Number (digits only)' },
  { value: WorkTaskQuestionType.FILE_TYPE, label: 'File Upload' },
  { value: WorkTaskQuestionType.FILE_TYPE_WITH_EXPIRY, label: 'File Upload with Expiry Date' },
  { value: WorkTaskQuestionType.DROP_DOWN_SELECT, label: 'Dropdown Select' },
  { value: WorkTaskQuestionType.MULTI_SELECT, label: 'Multi Select' },
  { value: WorkTaskQuestionType.ACTIVITY_TIME_GRID, label: 'Activity Time Grid' },
  { value: WorkTaskQuestionType.TABLE_GRID, label: 'Table (dynamic rows)' },
  { value: WorkTaskQuestionType.STATIC_TEXT, label: 'Static text (display only)' },
  { value: WorkTaskQuestionType.ATTRIBUTE, label: 'Attribute' },
] as const;

const getWorkTaskQuestionTypeLabel = (questionType: WorkTaskQuestionType | string): string => {
  const match: WorkTaskQuestionTypeOption | undefined = WORK_TASK_QUESTION_TYPES.find(
    (typeOption: WorkTaskQuestionTypeOption) => typeOption.value === questionType
  );
  return match?.label ?? String(questionType);
};

const isFileUploadWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.FILE_TYPE
    || questionType === WorkTaskQuestionType.FILE_TYPE_WITH_EXPIRY
  );
};

const defaultDocumentOwnershipForTemplatePurpose = (
  templatePurpose: WorkTaskTemplatePurpose | string
): WorkTaskFileDocumentOwnership => {
  if (templatePurpose === WorkTaskTemplatePurpose.CARE_PLAN) {
    return WorkTaskFileDocumentOwnership.CLIENT_OWNED;
  }
  return WorkTaskFileDocumentOwnership.WORKER_OWNED;
};

/**
 * Ownership is implied by the template purpose, so admins do not choose it:
 * worker onboarding file uploads are always worker-owned, and care plan
 * file uploads are always client-owned.
 */
const isDocumentOwnershipLockedByTemplatePurpose = (
  templatePurpose: WorkTaskTemplatePurpose | string | null | undefined
): boolean => {
  return (
    templatePurpose === WorkTaskTemplatePurpose.JOBBER_ONBOARDING
    || templatePurpose === 'JOBBER_ONBOARDING'
    || templatePurpose === WorkTaskTemplatePurpose.CARE_PLAN
    || templatePurpose === 'CARE_PLAN'
  );
};

const isStaticTextWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.STATIC_TEXT
    || questionType === 'STATIC_TEXT'
  );
};

const isTimeHhMmWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.TIME_HH_MM
    || questionType === 'TIME_HH_MM'
  );
};

const isDateYyyyMmDdWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.DATE_YYYY_MM_DD
    || questionType === 'DATE_YYYY_MM_DD'
  );
};

const isCanadianPostalCodeWorkTaskQuestionType = (
  questionType: WorkTaskQuestionType | string
): boolean => {
  return (
    questionType === WorkTaskQuestionType.CANADIAN_POSTAL_CODE
    || questionType === 'CANADIAN_POSTAL_CODE'
  );
};

const isNumberWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.NUMBER
    || questionType === 'NUMBER'
  );
};

const isAttributeWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.ATTRIBUTE
    || questionType === 'ATTRIBUTE'
  );
};

const isMultiSelectWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.MULTI_SELECT
    || questionType === 'MULTI_SELECT'
  );
};

/**
 * True when the answer is carried in textAnswer (plain text, dropdown, multi-select, time, date, postal, number, or attribute).
 */
const isTextAnswerWorkTaskQuestionType = (questionType: WorkTaskQuestionType | string): boolean => {
  return (
    questionType === WorkTaskQuestionType.TEXT_FIELD
    || questionType === 'TEXT_FIELD'
    || questionType === WorkTaskQuestionType.DROP_DOWN_SELECT
    || questionType === 'DROP_DOWN_SELECT'
    || isMultiSelectWorkTaskQuestionType(questionType)
    || isTimeHhMmWorkTaskQuestionType(questionType)
    || isDateYyyyMmDdWorkTaskQuestionType(questionType)
    || isCanadianPostalCodeWorkTaskQuestionType(questionType)
    || isNumberWorkTaskQuestionType(questionType)
    || isAttributeWorkTaskQuestionType(questionType)
  );
};

const isWorkTaskAttributeSingleSelectAnswerType = (
  attributeAnswerType: WorkTaskAttributeAnswerType | string | null | undefined
): boolean => {
  return (
    attributeAnswerType === WorkTaskAttributeAnswerType.SINGLE_SELECT
    || attributeAnswerType === 'SINGLE_SELECT'
  );
};

const isWorkTaskAttributeMultiSelectAnswerType = (
  attributeAnswerType: WorkTaskAttributeAnswerType | string | null | undefined
): boolean => {
  return (
    attributeAnswerType === WorkTaskAttributeAnswerType.MULTI_SELECT
    || attributeAnswerType === 'MULTI_SELECT'
  );
};

const isWorkTaskAttributeRatingAnswerType = (
  attributeAnswerType: WorkTaskAttributeAnswerType | string | null | undefined
): boolean => {
  return (
    attributeAnswerType === WorkTaskAttributeAnswerType.RATING
    || attributeAnswerType === 'RATING'
  );
};

const WORK_TASK_MULTI_SELECT_OPTION_ID_DELIMITER: string = ',';

/**
 * Decodes MULTI_SELECT ATTRIBUTE textAnswer (comma-separated optionIds) into option ids.
 */
const parseWorkTaskMultiSelectOptionIdsFromTextAnswer = (
  textAnswer: string | null | undefined
): readonly string[] => {
  if (!isValidString(textAnswer)) {
    return [];
  }
  return textAnswer
    .split(WORK_TASK_MULTI_SELECT_OPTION_ID_DELIMITER)
    .map((part: string): string => part.trim())
    .filter((part: string): boolean => isValidString(part));
};

/**
 * Encodes selected MULTI_SELECT optionIds into ATTRIBUTE textAnswer (comma-joined, no spaces required).
 */
const encodeWorkTaskMultiSelectOptionIdsToTextAnswer = (
  selectOptionIds: readonly string[]
): string => {
  return selectOptionIds
    .map((optionId: string): string => optionId.trim())
    .filter((optionId: string): boolean => isValidString(optionId))
    .join(WORK_TASK_MULTI_SELECT_OPTION_ID_DELIMITER);
};

const getWorkTaskAttributeOptionLabel = (
  attributeSelectOptions: readonly WorkTaskAttributeSelectOptionResponseDTO[] | null | undefined,
  optionId: string
): string => {
  const matchingOption: WorkTaskAttributeSelectOptionResponseDTO | undefined = (
    attributeSelectOptions ?? []
  ).find(
    (option: WorkTaskAttributeSelectOptionResponseDTO): boolean => option.optionId === optionId
  );
  if (matchingOption !== undefined && isValidString(matchingOption.label)) {
    return matchingOption.label;
  }
  return optionId;
};

const getWorkTaskSelectOptionLabel = (
  selectOptions: readonly WorkTaskSelectOptionResponseDTO[] | null | undefined,
  optionId: string
): string => {
  const matchingOption: WorkTaskSelectOptionResponseDTO | undefined = (selectOptions ?? []).find(
    (option: WorkTaskSelectOptionResponseDTO): boolean => option.optionId === optionId
  );
  if (matchingOption !== undefined && isValidString(matchingOption.label)) {
    return matchingOption.label;
  }
  return optionId;
};

/**
 * Formats MULTI_SELECT textAnswer (comma-joined optionIds) into display labels.
 */
const formatWorkTaskMultiSelectTextAnswerForDisplay = (
  textAnswer: string | null | undefined,
  selectOptions: readonly WorkTaskSelectOptionResponseDTO[] | null | undefined
): string => {
  if (!isValidString(textAnswer)) {
    return '';
  }
  const optionIds: readonly string[] = parseWorkTaskMultiSelectOptionIdsFromTextAnswer(textAnswer);
  if (optionIds.length < 1) {
    return textAnswer.trim();
  }
  return optionIds
    .map((optionId: string): string => getWorkTaskSelectOptionLabel(selectOptions, optionId))
    .join(', ');
};

/**
 * Formats ATTRIBUTE textAnswer for display: optionIds → labels for select types; ratings unchanged.
 */
const formatWorkTaskAttributeTextAnswerForDisplay = (
  textAnswer: string | null | undefined,
  question: Pick<
    WorkTaskQuestionResponseDTO,
    'attributeAnswerType' | 'attributeSelectOptions'
  >
): string => {
  if (!isValidString(textAnswer)) {
    return '';
  }
  const trimmedAnswer: string = textAnswer.trim();
  if (isWorkTaskAttributeRatingAnswerType(question.attributeAnswerType)) {
    return trimmedAnswer;
  }
  if (isWorkTaskAttributeMultiSelectAnswerType(question.attributeAnswerType)) {
    const optionIds: readonly string[] =
      parseWorkTaskMultiSelectOptionIdsFromTextAnswer(trimmedAnswer);
    if (optionIds.length < 1) {
      return trimmedAnswer;
    }
    return optionIds
      .map(
        (optionId: string): string =>
          getWorkTaskAttributeOptionLabel(question.attributeSelectOptions, optionId)
      )
      .join(', ');
  }
  if (isWorkTaskAttributeSingleSelectAnswerType(question.attributeAnswerType)) {
    return getWorkTaskAttributeOptionLabel(question.attributeSelectOptions, trimmedAnswer);
  }
  // Fallback when answer type is missing but options exist (legacy / incomplete snapshot).
  const optionIds: readonly string[] =
    parseWorkTaskMultiSelectOptionIdsFromTextAnswer(trimmedAnswer);
  if (optionIds.length > 1) {
    return optionIds
      .map(
        (optionId: string): string =>
          getWorkTaskAttributeOptionLabel(question.attributeSelectOptions, optionId)
      )
      .join(', ');
  }
  return getWorkTaskAttributeOptionLabel(question.attributeSelectOptions, trimmedAnswer);
};

const WORKER_SIGNATURE_REQUIRED_ATTRIBUTE: RequiredAttributeType = RequiredAttributeType.WORKER_SIG;
const CLIENT_SIGNATURE_REQUIRED_ATTRIBUTE: RequiredAttributeType = RequiredAttributeType.CLIENT_SIG;

const templateRequiresWorkerSignature = (
  requiredAttributes: readonly RequiredAttributeResponseDTO[] | null | undefined
): boolean => {
  return (requiredAttributes ?? []).some(
    (attribute: RequiredAttributeResponseDTO) =>
      attribute.requiredAttributeType === RequiredAttributeType.WORKER_SIG
      || attribute.requiredAttributeType === 'WORKER_SIG'
  );
};

const templateRequiresClientSignature = (
  requiredAttributes: readonly RequiredAttributeResponseDTO[] | null | undefined
): boolean => {
  return (requiredAttributes ?? []).some(
    (attribute: RequiredAttributeResponseDTO) =>
      attribute.requiredAttributeType === RequiredAttributeType.CLIENT_SIG
      || attribute.requiredAttributeType === 'CLIENT_SIG'
  );
};

const buildRequiredAttributesForTemplateCreate = (
  requiresWorkerSignature: boolean,
  requiresClientSignature: boolean
): readonly RequiredAttributeType[] => {
  const requiredAttributes: RequiredAttributeType[] = [];
  if (requiresWorkerSignature) {
    requiredAttributes.push(WORKER_SIGNATURE_REQUIRED_ATTRIBUTE);
  }
  if (requiresClientSignature) {
    requiredAttributes.push(CLIENT_SIGNATURE_REQUIRED_ATTRIBUTE);
  }
  return requiredAttributes;
};

export type {
  WorkTaskActivityTypeDefinitionRequestDTO,
  WorkTaskActivityTypeDefinitionResponseDTO,
  WorkTaskAttributeSelectOptionResponseDTO,
  WorkTaskSelectOptionRequestDTO,
  WorkTaskSelectOptionResponseDTO,
  WorkTaskTableColumnDefinitionRequestDTO,
  WorkTaskTableColumnDefinitionResponseDTO,
  WorkTaskSectionConditionRequestDTO,
  WorkTaskSectionConditionResponseDTO,
  WorkTaskQuestionVisibilityConditionRequestDTO,
  WorkTaskQuestionVisibilityConditionResponseDTO,
  WorkTaskQuestionRequestDTO,
  WorkTaskFormSectionRequestDTO,
  CreateWorkTaskTemplateRequestDTO,
  UpdateWorkTaskTemplateRequestDTO,
  RequiredAttributeResponseDTO,
  WorkTaskQuestionResponseDTO,
  WorkTaskFormSectionResponseDTO,
  WorkTaskTemplateMetaData,
  WorkTaskTemplateResponseDTO,
  WorkTaskQuestionTypeOption,
};

export {
  RequiredAttributeType,
  WorkTaskAttributeAnswerType,
  WorkTaskFileDocumentOwnership,
  WorkTaskPdfSectionVisibility,
  WorkTaskTemplatePurpose,
  WORK_TASK_QUESTION_TYPES,
  WORKER_SIGNATURE_REQUIRED_ATTRIBUTE,
  CLIENT_SIGNATURE_REQUIRED_ATTRIBUTE,
  buildRequiredAttributesForTemplateCreate,
  defaultDocumentOwnershipForTemplatePurpose,
  formatWorkTaskTemplateCreatedDisplay,
  formatWorkTaskTemplateCreatorDisplay,
  formatWorkTaskTemplateUpdatedDisplay,
  getWorkTaskTemplatePurposeDisplayLabel,
  getWorkTaskQuestionTypeLabel,
  isAttributeWorkTaskQuestionType,
  isDocumentOwnershipLockedByTemplatePurpose,
  isWorkerTaskTemplatePurpose,
  isFileUploadWorkTaskQuestionType,
  isMultiSelectWorkTaskQuestionType,
  isStaticTextWorkTaskQuestionType,
  isTextAnswerWorkTaskQuestionType,
  isTimeHhMmWorkTaskQuestionType,
  isDateYyyyMmDdWorkTaskQuestionType,
  isCanadianPostalCodeWorkTaskQuestionType,
  isNumberWorkTaskQuestionType,
  encodeWorkTaskMultiSelectOptionIdsToTextAnswer,
  formatWorkTaskAttributeTextAnswerForDisplay,
  formatWorkTaskMultiSelectTextAnswerForDisplay,
  isWorkTaskAttributeMultiSelectAnswerType,
  isWorkTaskAttributeRatingAnswerType,
  isWorkTaskAttributeSingleSelectAnswerType,
  parseWorkTaskMultiSelectOptionIdsFromTextAnswer,
  shouldDisplayWorkTaskTemplateLastUpdated,
  templateRequiresClientSignature,
  templateRequiresWorkerSignature,
  WorkTaskQuestionSubTypeOne,
  WorkTaskQuestionType,
};
